import type { CoatColor } from '../../utils/coatColor';
import type { CoatType } from '../../utils/coatType';
import type { GenomicMating, MatingCheck } from '../MatingCheckCard';

/** Wie im Backend (DnaSubjectTypes): ein erfasster Hund oder die Probe eines nicht erfassten Hundes. */
export type DnaSubjectType = 'Dog' | 'Sample';

export interface DnaSubjectRef {
  type: DnaSubjectType;
  id: number;
}

export interface DnaSubjectSummary extends DnaSubjectRef {
  name: string;
  sex: 'Female' | 'Male' | 'Unknown' | string;
  breedName: string | null;
  breedId: number | null;
  notes: string | null;
  dateOfBirth: string | null;
  microchipNumber: string | null;
  holderName: string | null;
  photoUrl: string | null;
  snpMarkers: number;
  strMarkers: number;
  hasRawData: boolean;
  hasTraits: boolean;
  geneticTests: number;
  coiPercent: number | null;
  createdAt: string | null;
}

export interface DnaUploadItem {
  index: number;
  category: string;
  scheme: string | null;
  lab: string | null;
  examinationDate: string | null;
  findingCount: number;
  affected: number;
  suspicious: number;
  carrier: number;
  unaffected: number;
  snpMarkers: number;
  strMarkers: number;
  coiPercent: number | null;
  breedAncestry: string | null;
}

export interface DnaDetectedAnimal {
  name: string | null;
  microchipNumber: string | null;
  sex: 'Female' | 'Male' | null;
  sampleId: string | null;
  lab: string | null;
  date: string | null;
}

export interface DnaDogMatch {
  dogId: number;
  name: string;
  reason: 'Microchip' | 'Name';
}

export interface DnaUploadAnalysis {
  recognized: boolean;
  fileName: string;
  fileSize: number;
  formName: string | null;
  problems: string[];
  items: DnaUploadItem[];
  snpMarkers: number;
  strMarkers: number;
  hasRawData: boolean;
  rawMarkerCount: number;
  coiPercent: number | null;
  detected: DnaDetectedAnimal;
  matchingDogs: DnaDogMatch[];
}

export interface DnaImportResult {
  targetType: DnaSubjectType;
  targetId: number;
  targetName: string;
  importedItems: number;
  skippedDuplicates: number;
  snpMarkers: number;
  strMarkers: number;
}

export interface DnaSource {
  category: string;
  scheme: string | null;
  lab: string | null;
  date: string | null;
  findingCount: number;
}

export interface DnaCoi {
  percent: number;
  source: string | null;
  computed: boolean;
}

export interface DnaFinding {
  name: string;
  result: string | null;
  detail: string | null;
}

export interface DnaGeneticDiversity {
  markers: number;
  heterozygosityPercent: number | null;
  lowPercent: number | null;
  highPercent: number | null;
  level: 'High' | 'Medium' | 'Low' | 'InsufficientMarkers';
}

export interface DnaOverview {
  subject: DnaSubjectSummary;
  sources: DnaSource[];
  coatColor: CoatColor | null;
  coatType: CoatType | null;
  diversity: DnaGeneticDiversity | null;
  coi: DnaCoi[];
  breedAncestry: string | null;
  heterozygosity: string | null;
  maternalLine: string | null;
  paternalLine: string | null;
  genetics: { tested: number; unaffected: number; carrier: number; suspicious: number; affected: number };
  notableFindings: DnaFinding[];
}

export type ParentageVerdict = 'Compatible' | 'Doubtful' | 'Excluded' | 'InsufficientMarkers' | 'NotComparable' | 'NoProfile';

export interface DnaParentageCheck {
  verdict: ParentageVerdict;
  markerKind: 'Snp' | 'Str' | null;
  comparedMarkers: number;
  conflicts: { code: string; name: string; puppyGenotype: string; parentGenotype: string }[];
}

export interface DnaComparison {
  first: DnaSubjectSummary;
  second: DnaSubjectSummary;
  firstAsParent: DnaParentageCheck;
  secondAsParent: DnaParentageCheck;
  isag: { comparedMarkers: number; kinship: number | null; kinshipLow: number | null; kinshipHigh: number | null; verdict: string } | null;
  genomic: { comparedMarkers: number; kinship: number; relationshipDegree: string; expectedPuppyCoiPercent: number | null } | null;
  /** Nur bei einer Huendin und einem Rueden: dieselbe Pruefung wie in der Wurfplanung. */
  mating: MatingCheck | null;
  matingGenomic: GenomicMating | null;
}

/** URL-Segment fuer die Detailseite: /dna/dog/12, /dna/sample/3. */
export const subjectPath = (subject: DnaSubjectRef) => `/dna/${subject.type.toLowerCase()}/${subject.id}`;

export const subjectKey = (subject: DnaSubjectRef) => `${subject.type}:${subject.id}`;

export const parseSubjectKey = (key: string): DnaSubjectRef | null => {
  const [type, id] = key.split(':');
  const numericId = Number(id);
  return (type === 'Dog' || type === 'Sample') && Number.isInteger(numericId) && numericId > 0 ? { type, id: numericId } : null;
};

export const typeFromPath = (segment: string | undefined): DnaSubjectType | null =>
  segment === 'dog' ? 'Dog' : segment === 'sample' ? 'Sample' : null;

/** Hat der Hund bzw. die Probe irgendwelche DNA-Daten? */
export const hasDnaData = (subject: DnaSubjectSummary) =>
  subject.snpMarkers > 0 || subject.strMarkers > 0 || subject.hasTraits || subject.geneticTests > 0 || subject.coiPercent != null;

export const unwrap = <T,>(res: any): T => (res && typeof res === 'object' && 'data' in res ? res.data : res) as T;
